import React from "react";
import type { Theme } from "@material-ui/core";
import { useMediaQuery } from "@material-ui/core";
import useTheme from "@material-ui/core/styles/useTheme";

import { createCtx } from "./context";

/** Mobile context is where only one child component should have capture
 * at one time. We only want one mobile panel/pane open at one time.
 */
export type MobileStore = {
  /** The component with current mobile capture */
  capture?: string;
  /** The current captured priority, higher should override */
  priority?: number;
  /** Indicator if the viewport is for mobile or not */
  isMobile: boolean;
};

export const [useMobileContext, MobileContextProvider] = createCtx<MobileStore | undefined>();
const MobileActionsContext = React.createContext<(mobileStore: Partial<MobileStore>) => void>(() => {});

/**
 * Mobile context for changing values of the mobile store
 *
 * @return {*}  {((mobileStore: Partial<MobileStore>) => void)}
 */
export const useMobileActionsContext = (): ((mobileStore: Partial<MobileStore>) => void) => {
  const context = React.useContext(MobileActionsContext);
  if (context === undefined) {
    throw new Error(`Context must be called within Provider`);
  }
  return context;
};

type Props = { children: React.ReactNode };

export const MobileProvider = ({ children }: Props): React.ReactElement => {
  const theme = useTheme();
  const sm = useMediaQuery<Theme>(theme.breakpoints.down("sm"));
  const [mobileStore, setMobileStore] = React.useState<MobileStore>({ isMobile: sm });

  return (
    <MobileContextProvider value={mobileStore}>
      <MobileActionsContext.Provider
        value={(store: Partial<MobileStore>) =>
          setMobileStore((prev: $TSFixMe) => ({
            ...prev,
            ...store,
            ...{ isMobile: sm },
          }))
        }
      >
        {children}
      </MobileActionsContext.Provider>
    </MobileContextProvider>
  );
};
